import { Close, CopyAllOutlined, Fullscreen } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Button, Spacer, Toolbar, globalMessageHandler } from "common";
import I18n from "i18n";
import moment from "moment";
import { ReactNode, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import {
  PaymentStatusResponse,
  generatePaymentUrl,
  getPaymentStatus,
  regeneratePaymentUrl,
  sendPaymentUrl,
} from "./bookingApi";

export type PaymentManagementDialogProps = {
  open: boolean;
  onClose: () => void;
  bookingId?: number;
  bookingEmail?: string;
  bookingData?: any;
  mode?: "admin" | "partner";
};

type BookingDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  bookingData?: any;
  url: string;
  paymentStatus?: PaymentStatusResponse;
  formatMoney: (amount?: any, currency?: any) => string;
  translateStatus: (status?: string | null) => { label: string; color: string };
  formatDateTime: (value: any) => string;
  lang: "hu" | "en";
};

const BookingDetailsModal = ({
  open,
  onClose,
  bookingData,
  url,
  paymentStatus,
  formatMoney,
  translateStatus,
  formatDateTime,
  lang,
}: BookingDetailsModalProps) => {
  const active = paymentStatus?.active_transaction as any;
  const status = translateStatus(active?.status);
  const amount = active?.amount;
  const currency = active?.currency;
  const paid = paymentStatus?.booking_paid;

  const t = (key: string) =>
    I18n.t(`Payment.${key}`, {
      locale: lang,
      defaultValue: `Payment.${key}`,
    });

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5">{t("paymentInfo")}</Typography>
          <Spacer right />
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            maxWidth: 600,
            margin: "0 auto",
            py: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>
              {t("booking")}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {bookingData?.booking_number && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    {t("bookingNumber")}:
                  </Typography>
                  <Typography fontWeight={600}>
                    {bookingData.booking_number}
                  </Typography>
                </Box>
              )}

              {bookingData?.program_variant?.display_name && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    {t("programType")}:
                  </Typography>
                  <Typography fontWeight={600}>
                    {bookingData.program_variant.display_name}
                  </Typography>
                </Box>
              )}

              {bookingData?.estimated_start_time && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    {t("startTime")}:
                  </Typography>
                  <Typography fontWeight={600}>
                    {formatDateTime(bookingData.estimated_start_time)}
                  </Typography>
                </Box>
              )}

              {bookingData?.name && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    {t("guestName")}:
                  </Typography>
                  <Typography fontWeight={600}>{bookingData.name}</Typography>
                </Box>
              )}

              {bookingData?.email && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">{t("email")}:</Typography>
                  <Typography fontWeight={600}>{bookingData.email}</Typography>
                </Box>
              )}

              {bookingData?.phone && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">{t("phone")}:</Typography>
                  <Typography fontWeight={600}>{bookingData.phone}</Typography>
                </Box>
              )}

              {bookingData?.number_of_guests && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">{t("guests")}:</Typography>
                  <Typography fontWeight={600}>
                    {bookingData.number_of_guests}
                  </Typography>
                </Box>
              )}

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">{t("booking")}:</Typography>
                <Typography fontWeight={700}>
                  {paid ? t("paid") : t("notPaid")}
                </Typography>
              </Box>

              {!!active && (
                <>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography color="text.secondary">
                      {t("linkStatus")}:
                    </Typography>
                    <Typography sx={{ color: status.color, fontWeight: 700 }}>
                      {status.label}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography color="text.secondary">
                      {t("amount")}:
                    </Typography>
                    <Typography fontWeight={700}>
                      {formatMoney(amount, currency)}
                    </Typography>
                  </Box>

                  {active?.expires_at && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography color="text.secondary">
                        {t("expires")}:
                      </Typography>
                      <Typography>
                        {formatDateTime(active.expires_at)}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>

          {!!url && (
            <Paper
              elevation={3}
              sx={{
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {t("paymentLink")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("validity")}: {formatDateTime(active?.expires_at)}
              </Typography>
              <QRCode value={url} size={256} />
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          sx={{ minWidth: 120 }}
          title={I18n.t("App.close", { locale: lang })}
          variant="outlined"
          onClick={onClose}
        />
      </DialogActions>
    </Dialog>
  );
};

type PartnerViewProps = {
  paymentStatus?: PaymentStatusResponse;
  email: string;
  setEmail: (email: string) => void;
  url: string;
  selectedLang: "hu" | "en";
  onLangChange: (lang: "hu" | "en") => void;
  copyPaymentUrl: (url: string) => void;
  formatMoney: (amount?: any, currency?: any) => string;
  translateStatus: (status?: string | null) => { label: string; color: string };
  formatDateTime: (value: any) => string;
  onShowDetails: (lang: "hu" | "en") => void;
};

const PartnerView = ({
  paymentStatus,
  email,
  setEmail,
  url,
  selectedLang,
  onLangChange,
  copyPaymentUrl,
  formatMoney,
  translateStatus,
  formatDateTime,
  onShowDetails,
}: PartnerViewProps) => {
  const active = paymentStatus?.active_transaction as any;
  const status = translateStatus(active?.status);
  const amount = active?.amount;
  const currency = active?.currency;
  const expiresAt = active?.expires_at;
  const paid = paymentStatus?.booking_paid;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          backgroundColor: "background.paper",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {I18n.t("Payment.paymentStatusTitle")}
        </Typography>
        <Toolbar sx={{ pl: 0, pr: 0, minHeight: "unset", gap: 2 }}>
          <Typography sx={{ color: "text.secondary" }}>
            {I18n.t("Payment.booking")}:
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {paid ? I18n.t("Payment.paid") : I18n.t("Payment.notPaid")}
          </Typography>
        </Toolbar>

        {!!active && (
          <Toolbar sx={{ pl: 0, pr: 0, minHeight: "unset", gap: 2 }}>
            <Typography sx={{ color: "text.secondary" }}>
              {I18n.t("Payment.linkStatus")}:
            </Typography>
            <Typography sx={{ color: status.color, fontWeight: 700 }}>
              {status.label}
            </Typography>
            <Spacer right />
            <Typography sx={{ color: "text.secondary" }}>
              {I18n.t("Payment.amount")}:
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatMoney(amount, currency)}
            </Typography>
          </Toolbar>
        )}

        {!!expiresAt && (
          <Typography variant="body2" color="text.secondary">
            {I18n.t("Payment.expires")}: {formatDateTime(expiresAt)}
          </Typography>
        )}
      </Paper>

      <TextField
        label={I18n.t("Payment.email")}
        value={email}
        onChange={e => setEmail(e.target.value)}
        fullWidth
      />

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {I18n.t("Payment.languageLabel")}
        </Typography>
        <Button
          variant={selectedLang === "hu" ? "contained" : "outlined"}
          title="HU"
          onClick={() => onLangChange("hu")}
        />
        <Button
          variant={selectedLang === "en" ? "contained" : "outlined"}
          title="EN"
          onClick={() => onLangChange("en")}
        />
      </Box>

      {!!url && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Toolbar sx={{ gap: 1, pl: 0, pr: 0 }}>
            <TextField
              label={I18n.t("Payment.paymentLink")}
              value={url}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <IconButton
              title={I18n.t(
                selectedLang === "hu" ? "Payment.copyHu" : "Payment.copyEn",
              )}
              onClick={() => copyPaymentUrl(url)}
            >
              <CopyAllOutlined />
            </IconButton>
          </Toolbar>

          <Divider />

          <Toolbar
            sx={{
              pl: 0,
              pr: 0,
              justifyContent: "center",
              minHeight: "unset",
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "background.default",
              }}
            >
              <QRCode value={url} size={180} />
            </Paper>
          </Toolbar>
        </Paper>
      )}

      {!!url && !email.trim() && (
        <Alert severity="warning">
          {I18n.t("Payment.missingEmailWarning")}
        </Alert>
      )}

      {!!url && (
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<Fullscreen />}
            onClick={() => onShowDetails(selectedLang)}
            fullWidth
            title={I18n.t("Payment.showQrForGuest", { locale: "hu" })}
          />
        </Box>
      )}
    </>
  );
};

export default function PaymentManagementDialog({
  open,
  onClose,
  bookingId,
  bookingEmail,
  bookingData,
  mode = "admin",
}: PaymentManagementDialogProps) {
  const [email, setEmail] = useState<string>((bookingEmail || "").trim());
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLang, setDetailsLang] = useState<"hu" | "en">("hu");
  const [selectedLang, setSelectedLang] = useState<"hu" | "en">("hu");

  useEffect(() => {
    if (!open) {
      return;
    }
    setEmail((bookingEmail || "").trim());
  }, [open, bookingEmail]);

  const buildLangUrl = (baseUrl: string, lang: "hu" | "en") => {
    if (!baseUrl) {
      return "";
    }
    try {
      const parsed = new URL(baseUrl);
      parsed.searchParams.set("lang", lang);
      return parsed.toString();
    } catch {
      const joiner = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${joiner}lang=${lang}`;
    }
  };

  const formatDateTime = (value: any) => {
    if (!value) {
      return "—";
    }
    const dt = moment(value);
    return dt.isValid() ? dt.format("L LT") : String(value);
  };

  const translateStatus = (status?: string | null) => {
    if (!status) {
      return { label: "—", color: "text.secondary" as const };
    }
    const normalized = status.toLowerCase();
    switch (normalized) {
      case "created":
        return { label: "Létrehozva", color: "warning.main" as const };
      case "pending":
        return { label: "Függőben", color: "warning.main" as const };
      case "paid":
        return { label: "Fizetve", color: "success.main" as const };
      case "failed":
        return { label: "Sikertelen", color: "error.main" as const };
      case "cancelled":
      case "canceled":
        return { label: "Visszavonva", color: "error.main" as const };
      case "expired":
        return { label: "Lejárt", color: "error.main" as const };
      default:
        return { label: status, color: "text.primary" as const };
    }
  };

  const formatMoney = (amount?: any, currency?: any) => {
    if (amount === null || amount === undefined || amount === "") {
      return "—";
    }

    const numeric = typeof amount === "number" ? amount : Number(amount);
    if (!Number.isFinite(numeric)) {
      return String(amount);
    }

    if (currency === "EUR") {
      return I18n.numberToCurrency(numeric, {
        precision: 2,
        unit: "€",
        format: "%u %n",
        separator: ",",
        delimiter: " ",
        stripInsignificantZeros: true,
      });
    }

    return `${numeric} ${currency || ""}`.trim();
  };

  const TransactionDetails = ({
    transaction,
  }: {
    transaction: Record<string, any>;
  }) => {
    const status = translateStatus(transaction?.status);

    const rows = useMemo(() => {
      const url = transaction?.url as string | undefined;
      const canShowUrl = !!url;

      const valueOrDash = (value: any): ReactNode => {
        if (value === null || value === undefined || value === "") {
          return "—";
        }
        return String(value);
      };

      return [
        { label: "Azonosító", value: valueOrDash(transaction?.id) },
        {
          label: "Státusz",
          value: (
            <Typography sx={{ color: status.color, fontWeight: 600 }}>
              {status.label}
            </Typography>
          ),
        },
        {
          label: "Összeg",
          value: formatMoney(transaction?.amount, transaction?.currency),
        },
        {
          label: "Pénznem",
          value: valueOrDash(transaction?.currency),
        },
        {
          label: "Fizetési kapu",
          value: valueOrDash(transaction?.gateway),
        },
        {
          label: "Token",
          value: (
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
            >
              {valueOrDash(transaction?.token)}
            </Typography>
          ),
        },
        {
          label: "Lejárat",
          value: formatDateTime(transaction?.expires_at),
        },
        {
          label: "Fizetve ekkor",
          value: formatDateTime(transaction?.paid_at),
        },
        {
          label: "Hiba oka",
          value: valueOrDash(transaction?.failure_reason),
          hideIfDash: true,
        },
        {
          label: "Stripe session",
          value: valueOrDash(transaction?.stripe_session_id),
          hideIfDash: true,
        },
        {
          label: "Stripe payment intent",
          value: valueOrDash(transaction?.stripe_payment_intent_id),
          hideIfDash: true,
        },
        {
          label: "Fizetési link",
          value: canShowUrl ? (
            <Toolbar sx={{ pl: 0, pr: 0, minHeight: "unset", gap: 1 }}>
              <Typography sx={{ wordBreak: "break-word" }}>{url}</Typography>
              <IconButton
                size="small"
                title="Másolás"
                onClick={() => copyPaymentUrl(url)}
              >
                <CopyAllOutlined fontSize="small" />
              </IconButton>
            </Toolbar>
          ) : (
            "—"
          ),
          hideIfDash: true,
        },
        {
          label: "Létrehozva",
          value: formatDateTime(transaction?.created_at),
          hideIfDash: true,
        },
        {
          label: "Módosítva",
          value: formatDateTime(transaction?.updated_at),
          hideIfDash: true,
        },
      ].filter(
        r => !(r as any).hideIfDash || (r.value !== "—" && r.value !== null),
      );
    }, [status.color, status.label, transaction]);

    return (
      <TableContainer
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableBody
            sx={{
              "& tr:nth-of-type(odd) td": {
                backgroundColor: "action.hover",
              },
              "& td": {
                verticalAlign: "top",
              },
            }}
          >
            {rows.map(row => (
              <TableRow key={row.label}>
                <TableCell sx={{ width: 200, color: "text.secondary" }}>
                  {row.label}
                </TableCell>
                <TableCell sx={{ wordBreak: "break-word" }}>
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const [paymentStatusLoading, setPaymentStatusLoading] = useState(false);
  const [paymentActionLoading, setPaymentActionLoading] = useState<
    "generate" | "regenerate" | "send" | null
  >(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusResponse>();
  const [lastPaymentUrl, setLastPaymentUrl] = useState<string>("");

  const copyPaymentUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      globalMessageHandler.snack({
        severity: "success",
        message: "Fizetési link vágólapra másolva.",
      });
    } catch {
      globalMessageHandler.alert({
        severity: "info",
        title: "Fizetési link",
        message: url,
      });
    }
  };

  const confirmAsync = (message: string) =>
    new Promise<boolean>(resolve => {
      globalMessageHandler.confirm(
        { title: "Megerősítés", message, severity: "warning" },
        () => resolve(true),
        () => resolve(false),
      );
    });

  const loadPaymentStatus = async () => {
    if (!bookingId) {
      return;
    }
    setPaymentStatusLoading(true);
    try {
      const response = await getPaymentStatus({ booking_id: bookingId });
      if (!response?.success) {
        globalMessageHandler.alert({
          severity: "error",
          message: "Nem sikerült lekérni a fizetési státuszt.",
        });
        return;
      }
      setPaymentStatus(response.data);
    } finally {
      setPaymentStatusLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setLastPaymentUrl("");
    loadPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  const handleCreateOrRegenerate = async (mode: "generate" | "regenerate") => {
    if (!bookingId) {
      return;
    }

    const ok = await confirmAsync(
      mode === "generate"
        ? "Fizetési link generálása?"
        : "Fizetési link újragenerálása? (A korábbi linkek érvénytelenné válhatnak.)",
    );
    if (!ok) {
      return;
    }

    setPaymentActionLoading(mode);
    try {
      const response =
        mode === "generate"
          ? await generatePaymentUrl({
              booking_id: bookingId,
              lang: selectedLang,
            })
          : await regeneratePaymentUrl({
              booking_id: bookingId,
              lang: selectedLang,
            });

      if (!response?.success) {
        globalMessageHandler.alert({
          severity: "error",
          message: "Nem sikerült a fizetési linket elkészíteni.",
        });
        return;
      }

      const url = response?.data?.url;
      if (!url) {
        globalMessageHandler.alert({
          severity: "error",
          message: "A szerver nem adott vissza fizetési linket.",
        });
        return;
      }

      setLastPaymentUrl(url);
      await copyPaymentUrl(url);
      await loadPaymentStatus();
    } finally {
      setPaymentActionLoading(null);
    }
  };

  const handleSendPaymentUrl = async (lang?: "hu" | "en") => {
    if (!bookingId) {
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      globalMessageHandler.alert({
        severity: "warning",
        title: "Hiányzó email cím",
        message:
          "A foglaláson nincs email cím, ezért nem küldhető ki a fizetési link.",
      });
      return;
    }
    const ok = await confirmAsync(
      `Fizetési link elküldése erre az email címre?\n${trimmedEmail}`,
    );
    if (!ok) {
      return;
    }

    setPaymentActionLoading("send");
    try {
      const response = await sendPaymentUrl({
        booking_id: bookingId,
        email: trimmedEmail,
        lang: lang || selectedLang,
      });

      if (!response?.success) {
        globalMessageHandler.alert({
          severity: "error",
          message: "Nem sikerült elküldeni a fizetési linket emailben.",
        });
        return;
      }

      globalMessageHandler.snack({
        severity: "success",
        message: "Fizetési link elküldve.",
      });
    } finally {
      setPaymentActionLoading(null);
    }
  };

  const baseUrl =
    lastPaymentUrl ||
    ((paymentStatus?.active_transaction as any)?.url as string) ||
    ((paymentStatus?.latest_transaction as any)?.url as string) ||
    "";

  const url = buildLangUrl(baseUrl, selectedLang);
  const detailsUrl = buildLangUrl(baseUrl, detailsLang);

  const hasAnyTransaction =
    !!paymentStatus?.active_transaction || !!paymentStatus?.latest_transaction;

  const canSendEmail =
    !!email.trim() && (!!paymentStatus?.active_transaction || !!url);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === "partner" ? "Fizetési link" : "Fizetés kezelése"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {paymentStatusLoading && (
          <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            Státusz betöltése...
          </Typography>
        )}

        {mode === "partner" ? (
          <PartnerView
            paymentStatus={paymentStatus}
            email={email}
            setEmail={setEmail}
            url={url}
            selectedLang={selectedLang}
            onLangChange={setSelectedLang}
            copyPaymentUrl={copyPaymentUrl}
            formatMoney={formatMoney}
            translateStatus={translateStatus}
            formatDateTime={formatDateTime}
            onShowDetails={lang => {
              setDetailsLang(lang);
              setDetailsModalOpen(true);
            }}
          />
        ) : (
          <>
            {!!paymentStatus && (
              <>
                <Typography>
                  Fizetve: {paymentStatus.booking_paid ? "Igen" : "Nem"}
                </Typography>

                <Divider />

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Aktív tranzakció</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {paymentStatus.active_transaction ? (
                      <TransactionDetails
                        transaction={paymentStatus.active_transaction as any}
                      />
                    ) : (
                      <Typography variant="body2">
                        Nincs aktív tranzakció.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Legutóbbi tranzakció</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {paymentStatus.latest_transaction ? (
                      <TransactionDetails
                        transaction={paymentStatus.latest_transaction as any}
                      />
                    ) : (
                      <Typography variant="body2">Nincs tranzakció.</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {I18n.t("Payment.languageLabel")}
              </Typography>
              <Button
                variant={selectedLang === "hu" ? "contained" : "outlined"}
                title="HU"
                onClick={() => setSelectedLang("hu")}
              />
              <Button
                variant={selectedLang === "en" ? "contained" : "outlined"}
                title="EN"
                onClick={() => setSelectedLang("en")}
              />
            </Box>

            {!!baseUrl && (
              <Toolbar sx={{ gap: 1, pl: 0, pr: 0 }}>
                <TextField
                  label={I18n.t("Payment.paymentLink")}
                  value={url}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <IconButton
                  title={I18n.t(
                    selectedLang === "hu" ? "Payment.copyHu" : "Payment.copyEn",
                  )}
                  onClick={() => copyPaymentUrl(url)}
                >
                  <CopyAllOutlined />
                </IconButton>
              </Toolbar>
            )}

            {!!paymentStatus?.active_transaction && !email.trim() && (
              <Alert severity="warning">
                A foglaláson nincs email cím, ezért nem küldhető ki a fizetési
                link.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        <Button
          variant="outlined"
          title="Státusz frissítése"
          onClick={loadPaymentStatus}
          disabled={paymentStatusLoading || paymentActionLoading !== null}
        />

        {mode === "partner" ? (
          <Button
            variant="outlined"
            title="Fizetési link készítése"
            loading={
              paymentActionLoading === "generate" ||
              paymentActionLoading === "regenerate"
            }
            disabled={paymentActionLoading !== null}
            onClick={() =>
              handleCreateOrRegenerate(
                hasAnyTransaction ? "regenerate" : "generate",
              )
            }
          />
        ) : hasAnyTransaction ? (
          <Button
            variant="outlined"
            title="Fizetési link újragenerálása"
            color="warning"
            loading={paymentActionLoading === "regenerate"}
            disabled={paymentActionLoading !== null}
            onClick={() => handleCreateOrRegenerate("regenerate")}
          />
        ) : (
          <Button
            variant="outlined"
            title="Fizetési link generálása"
            loading={paymentActionLoading === "generate"}
            disabled={paymentActionLoading !== null}
            onClick={() => handleCreateOrRegenerate("generate")}
          />
        )}

        {(mode === "partner" ? !!url : !!paymentStatus?.active_transaction) && (
          <Button
            variant="outlined"
            title={
              email.trim() ? `Email küldés (${email.trim()})` : "Email küldés"
            }
            loading={paymentActionLoading === "send"}
            disabled={paymentActionLoading !== null || !canSendEmail}
            onClick={() => {
              void handleSendPaymentUrl();
            }}
          />
        )}

        <Spacer right />
        <Button
          title={I18n.t("App.close")}
          variant="outlined"
          onClick={onClose}
        />
      </DialogActions>

      <BookingDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        bookingData={bookingData}
        url={detailsUrl}
        paymentStatus={paymentStatus}
        formatMoney={formatMoney}
        translateStatus={translateStatus}
        formatDateTime={formatDateTime}
        lang={detailsLang}
      />
    </Dialog>
  );
}
